<template>
  <v-select
    v-model="value"
    ref="selectRef"
    class="e-select"
    hide-details="auto"
    variant="outlined"
    :clearable="clearable"
    :label="label"
    :return-object="returnObject"
    :item-title="itemTitle"
    :item-value="itemValue"
    :disabled="disabled"
    :readonly="readonly"
    :items="items"
    :multiple="multiple"
    :menu-props="{
      contentClass: ['e-select__content', { 'e-select__content--error': isError, 'menu-cover': menuCover }],
    }"
  >
    <template v-if="slots['prepend-inner']" #prepend-inner>
      <slot name="prepend-inner" />
    </template>

    <template #item="{ item, index, props }">
      <template v-if="!isObject(item.raw) || !item.raw?.hidden">
        <v-list-item
          v-bind="props"
          :disabled="isObject(item.raw) ? getNestedObjectProperty(item.raw, disabledItemKey!) : false"
          @click="onItemClick(item)"
        >
          <slot v-if="slots.item" name="item" :item="item" :index="index" />
          <div v-else class="v-list-item-title e-list-item-title d-flex align-center">
            <e-checkbox v-if="multiple" :model-value="isSelected(item)" />
            {{ isObject(item.raw) ? getNestedObjectProperty(item.raw, itemTitle) : item.raw }}
          </div>
        </v-list-item>
      </template>
    </template>

    <template v-if="slots.label" #label>
      <slot name="label" />
    </template>

    <template #no-data>
      <v-list-item>
        <div class="v-list-item-title e-list-item-title">Даные отсутствуют</div>
      </v-list-item>
    </template>
    <template v-if="slots.selection" #selection="{ item, index }">
      <slot name="selection" :item="item" :index="index" />
    </template>
    <template v-if="slots['prepend-item']" #prepend-item>
      <slot name="prepend-item" />
    </template>
  </v-select>
</template>

<script
  setup
  lang="ts"
  generic="
    T extends Dictionary<unknown> | string | number | null,
    K extends T extends Dictionary<unknown> ? Paths<T> | undefined : undefined,
    M extends boolean | undefined = undefined
  "
>
import { ref, toRef } from 'vue';
import type { SelectionValue } from '@/core/types/components/components';
import type { ListItem } from '@/core/types/components/selection';
import type { Dictionary, Paths } from '@/core/types/gengeric';
import { ECheckbox } from '@/core/components/ui';
import { getNestedObjectProperty } from '@/core/utils/object';
import { isObject } from '@/core/utils/type-guards';

const props = withDefaults(
  defineProps<{
    items: T[];
    label?: string;
    returnObject?: boolean;
    itemTitle?: string;
    itemValue?: K;
    disabled?: boolean;
    readonly?: boolean;
    clearable?: boolean;
    menuCover?: boolean;
    multiple?: M;
    disabledItemKey?: string;
  }>(),
  {
    itemTitle: 'title',
    disabledItemKey: 'disabled',
  },
);

const slots = defineSlots<{
  selection?: (props: { item: ListItem<T>; index: number }) => void;
  'prepend-inner'?: (props: unknown) => void;
  'prepend-item'?: (props: unknown) => void;
  item?: (props: { item: ListItem<T>; index: number }) => void;
  label?: () => void;
}>();

const emit = defineEmits<{
  (e: 'itemClick', item: ListItem<T>): void;
}>();

const value = defineModel<(M extends true ? SelectionValue<T, K>[] : SelectionValue<T, K>) | null | undefined>({
  required: true,
});

const isSelected = (item: ListItem<T>): boolean => {
  if (!props.multiple || !item.raw || !Array.isArray(value.value)) {
    return false;
  }

  if (props.itemValue && isObject(item.raw)) {
    const nestedValue = getNestedObjectProperty(item.raw, props.itemValue);
    return !!nestedValue && !!value.value?.includes(nestedValue);
  }

  return !!value.value?.includes(item.raw as SelectionValue<T, K>);
};

const selectRef = ref();

const isError = toRef(() => selectRef.value && selectRef.value?.isValid === false);

const onItemClick = (item: ListItem<T>) => {
  emit('itemClick', item);
};
</script>

<style scoped lang="scss">
:global(.e-select__content .v-list-item-title:not(.e-list-item-title)) {
  display: none !important;
}
</style>
